import _ from 'lodash'

import { evaluateFormula } from './evaluation'
import { Query } from '../models/query'
import { SyncLRUCacheWithTTL } from '../models/cache'

/**
 * Resolves a select/tags field's dropdown options from its `optionSource`, in
 * precedence order: (1) `referenceList` — a firm-owned vocabulary
 * (`core_config_referenceList`); (2) `distinctValues` — values already stored on
 * the field (grouped query); (3) the field's static `enum`/`suggestions`
 * (handled by the caller). Platform-agnostic; the React hook lives in the
 * webapp, mobile calls these directly. See schemas/entities/config/referenceList.json.
 */

export const REFERENCE_LIST_ENTITY_TYPE = '/1.0/entities/metadata/core_config_referenceList.json'
const KEY_PATH = 'core_config_referenceList.key'
const ITEMS_PATH = 'core_config_referenceList.items'
const OWNER_FIRM_ID_PATH = 'owner.firm.entityId'
const DEFAULT_DISTINCT_VALUES_SIZE = 100
/** A firm's reference lists are few; one page is enough to know which keys are taken. */
const EXISTING_KEYS_SIZE = 500

export interface IReferenceListOption {
  label: string
  value: string
}

export interface IOptionSource {
  /** Key of a firm-owned reference list to resolve options from. */
  referenceList?: string
  /**
   * Display name for the list this field binds to, shown when offering the key as
   * something to create. Defaults to the humanized key.
   */
  referenceListLabel?: string
  /**
   * Offer the *eligible keys* rather than a list's values — the authoring side.
   * Set on `core_config_referenceList.key` so the "New Reference List" picker is
   * derived from the fields that declared `referenceList`, never from a registry.
   * See collectReferenceListKeys.
   */
  referenceListKeys?: boolean
  /** When true, suggest the distinct values already stored on the bound field. */
  distinctValues?: boolean
  /**
   * Edge path whose target's OWNER firm is the vocabulary authority — set this
   * for counterparty-owned entities where the record owner isn't the firm whose
   * list you want (e.g. a carrier-owned check-in execution whose list belongs to
   * the facility). Falls back to the entity's owner firm, then the session firm,
   * when unset or unresolvable. See resolveReferenceListFirmId.
   */
  referenceListFirmEdge?: string
}

/**
 * Reads a field's `optionSource`, from an explicit prop or (the common case) the
 * bound schema property via the `dataSchema` frames context.
 */
export const getOptionSource = (props: { optionSource?: IOptionSource; frames?: any }): IOptionSource | undefined => {
  if (props?.optionSource) {
    return props.optionSource
  }
  const dataSchema = props?.frames?.getContext?.('dataSchema')
  return dataSchema?.optionSource
}

/** Convenience: the reference-list key of a field's optionSource, if any. */
export const getReferenceListKey = (props: { optionSource?: IOptionSource; frames?: any }): string | undefined =>
  getOptionSource(props)?.referenceList

/** Resolves the entity-type id for the entity a field is bound to (for value queries). */
export const resolveEntityTypeId = (entity: any, entityType: string | undefined, valuePath: string[]): string => {
  if (entityType) {
    return entityType
  }
  const namespace = _.first(valuePath)
  const schema = _.find(entity?.schemas, (s) => _.get(s, 'metadata.namespace') === namespace)
  return schema?.id ?? `/1.0/entities/metadata/${namespace}.json`
}

/**
 * The firm that owns the entity being edited — the list resolves against this
 * firm, not the session firm, so a cross-firm switcher sees the owner firm's
 * vocabulary. Undefined for a brand-new entity (query falls back to the session firm).
 */
export const getEntityOwnerFirmId = (entity: any): string | undefined =>
  entity?.get?.(OWNER_FIRM_ID_PATH) ?? _.get(entity, OWNER_FIRM_ID_PATH)

/**
 * Resolves the firm whose reference list governs a field on the fallback ladder:
 * owner firm of the entity at `referenceListFirmEdge` (resolved store-first,
 * cache-first) → this entity's owner firm → session firm. Any miss degrades to
 * the next rung. See {@link IOptionSource.referenceListFirmEdge}.
 */
export const resolveReferenceListFirmId = (
  apis: any,
  entity: any,
  optionSource?: IOptionSource,
): Promise<string | undefined> => {
  const ownerFirmId = getEntityOwnerFirmId(entity)
  const edgePath = optionSource?.referenceListFirmEdge
  if (!edgePath) {
    return Promise.resolve(ownerFirmId)
  }
  const edge = entity?.get?.(edgePath) ?? _.get(entity, edgePath)
  const targetId = _.get(edge, 'entityId')
  const store = apis?.getStore?.()
  if (!targetId || !store?.getOrFetchRecord) {
    return Promise.resolve(ownerFirmId)
  }
  return Promise.resolve(store.getOrFetchRecord(targetId))
    .then((target: any) => getEntityOwnerFirmId(target) ?? ownerFirmId)
    // An unresolvable edge is a defined rung of the ladder, not an error — drop to the owner firm.
    .catch(() => ownerFirmId)
}

/**
 * Active {value,label} options in stored array order (ordered by drag; no sortOrder field).
 * `value` is a non-empty string by schema (referenceList.json enforces minLength); label falls
 * back to value.
 */
export const mapReferenceListItemsToOptions = (items: any[]): IReferenceListOption[] =>
  _.chain(items)
    .filter((item) => item?.isActive !== false)
    .map((item) => ({ value: item.value, label: item.label || item.value }))
    .value()

/* Options cache: dedupe + reuse the resolved promise per firm+key so remounting selects don't
 * refetch. Same-session edits are evicted precisely by installReferenceListCacheInvalidation
 * (store save events); logout/firm-switch clears all. See .claude/docs/reference-lists.md. */

/**
 * Staleness ceiling for edits this store never hears about: a list edited in another
 * session/tab/client emits no RECORD_CHANGED here, so its cached options can be at most this
 * stale. Same-session edits don't wait on it — they invalidate on save. Capacity is a memory
 * bound only.
 */
export const REFERENCE_LIST_OPTIONS_CACHE_TTL_MS = 5 * 60 * 1000
const OPTIONS_CACHE_CAPACITY = 200
const optionsCache = new SyncLRUCacheWithTTL<string, Promise<IReferenceListOption[]>>(OPTIONS_CACHE_CAPACITY)

const optionsCacheKey = (referenceListKey: string, firmId?: string): string => `${firmId ?? ''}|${referenceListKey}`

/**
 * A firm's reference list by key as active {value,label} options, scoped to `firmId`.
 * Resolves to [] when no list is configured (callers fall back to suggestions).
 * Cached + deduped per firm+key. A load failure rejects (the caller surfaces it) and is evicted,
 * not cached — this is an authoritative firm vocabulary, so a silent empty menu would hide it.
 */
export const fetchReferenceListOptions = (
  apis: any,
  referenceListKey?: string,
  firmId?: string,
): Promise<IReferenceListOption[]> => {
  if (!referenceListKey) {
    return Promise.resolve([])
  }
  const cacheKey = optionsCacheKey(referenceListKey, firmId)
  const cached = optionsCache.get(cacheKey)
  if (cached) {
    return cached
  }
  const query = new Query(apis)
    .setEntityType(REFERENCE_LIST_ENTITY_TYPE)
    .setFilters([{ type: 'match', path: KEY_PATH, value: referenceListKey }])
    .setSize(1)
    .setDebug({ context: 'referenceListOptions' })
  if (firmId) {
    query.setFirm(firmId)
  }
  const collection = query.getCollection()
  const request = collection
    .find()
    .then((entities: any[]) => mapReferenceListItemsToOptions(_.get(_.first(entities), ITEMS_PATH) ?? []))
    .catch((error: any) => {
      // Evict so the next mount retries, and re-throw so the caller can show a load error.
      optionsCache.delete(cacheKey)
      throw error
    })
    .finally(() => collection.dispose())
  optionsCache.set(cacheKey, request, REFERENCE_LIST_OPTIONS_CACHE_TTL_MS)
  return request
}

/** Bust cached options: no key clears all (logout/firm-switch); a key+firmId drops one list. */
export const clearReferenceListOptionsCache = (referenceListKey?: string, firmId?: string): void => {
  if (referenceListKey == null) {
    optionsCache.clear()
    return
  }
  optionsCache.delete(optionsCacheKey(referenceListKey, firmId))
}

const invalidationInstalledStores = new WeakSet<object>()

/**
 * Bust a list's cached options when it's saved, via the store's save events
 * (`KEY_PATH` self-filters to reference-list entities). Call once at startup.
 */
export const installReferenceListCacheInvalidation = (store: any): void => {
  if (!store?.on || invalidationInstalledStores.has(store)) {
    return
  }
  invalidationInstalledStores.add(store)
  const onRecordSaved = (record: any) => {
    const key = record?.get?.(KEY_PATH)
    if (!_.isString(key) || key === '') {
      return
    }
    clearReferenceListOptionsCache(key, getEntityOwnerFirmId(record))
  }
  store.on('RECORD_CHANGED', onRecordSaved)
  store.on('RECORD_CREATED', onRecordSaved)
}

/* --------------------------------------------------------------------------
 * Authoring side — which lists a firm can create.
 *
 * Bindable keys are DERIVED from the firm's own loaded schemas (no registry on the
 * list itself), so they track bundle membership. See .claude/rules/webapp.md.
 * ------------------------------------------------------------------------ */

export interface IReferenceListKeyOption {
  /** The key a field binds to, e.g. 'trailerTags'. */
  value: string
  /** Picker label, e.g. 'Trailer Tags' — the list name, not suffixed by entity type. */
  label: string
  /** Where it's declared, e.g. 'Trailer · Tags'. */
  usedBy?: string
}

/** 'trailerTags' -> 'Trailer Tags'. */
export const humanizeReferenceListKey = (key: string): string => _.startCase(key)

/** Subtrees with no data declaration; skipping `uiSchema` avoids double-counting a key. */
const NON_DATA_KEYS = new Set(['uiSchema', 'translations'])

/**
 * Recurses a schema collecting `optionSource.referenceList` declarations. Generic,
 * not `properties`-only: keys also live under `definitions`/`$ref`/`items`/`allOf`.
 * First declaration wins (callers sort input for stability).
 */
const collectFromNode = (
  node: any,
  schemaTitle: string | undefined,
  fieldLabel: string | undefined,
  out: Map<string, IReferenceListKeyOption>,
  seen: WeakSet<object>
): void => {
  if (!_.isObject(node) || seen.has(node)) {
    return
  }
  seen.add(node)

  const optionSource: IOptionSource | undefined = _.get(node, 'optionSource')
  const key = optionSource?.referenceList
  if (_.isString(key) && key !== '' && !out.has(key)) {
    const name = optionSource?.referenceListLabel ?? humanizeReferenceListKey(key)
    const usedBy = _.compact([schemaTitle, fieldLabel]).join(' · ')
    // Label is the list name only (no entity suffix) — a key can span entities.
    out.set(key, {
      value: key,
      label: name,
      usedBy: usedBy === '' ? undefined : usedBy,
    })
  }

  _.forEach(node, (child, childKey) => {
    if (NON_DATA_KEYS.has(childKey as string) || childKey === 'optionSource' || !_.isObject(child)) {
      return
    }
    collectFromNode(child, schemaTitle, _.get(child, 'label') ?? fieldLabel, out, seen)
  })
}

/**
 * Every list any field in these schemas could bind to. Takes raw schema JSON
 * (unwrap store Entity `.content` first). Sorted by title so a shared key's
 * provenance is the same for every firm regardless of bundle order.
 */
export const collectReferenceListKeys = (schemas: any[]): IReferenceListKeyOption[] => {
  const out = new Map<string, IReferenceListKeyOption>()
  const ordered = _.sortBy(_.filter(schemas, _.isObject), (schema) => _.toLower(_.get(schema, 'title') ?? ''))
  // A fresh `seen` per schema: two schemas may share a spliced-in mixin subtree,
  // and one shared set would silently stop walking the second.
  _.forEach(ordered, (schema) =>
    collectFromNode(schema, _.get(schema, 'title'), undefined, out, new WeakSet<object>())
  )
  return _.sortBy([...out.values()], 'label')
}

/** Keys the firm already has a list for, so they aren't offered again. */
export const fetchExistingReferenceListKeys = (apis: any, firmId?: string): Promise<string[]> => {
  const query = new Query(apis)
    .setEntityType(REFERENCE_LIST_ENTITY_TYPE)
    .setSize(EXISTING_KEYS_SIZE)
    .setDebug({ context: 'referenceListExistingKeys' })
  // Undefined firmId = resolve in the caller's own firm (the common, non-cross-firm case).
  if (firmId) {
    query.setFirm(firmId)
  }
  const collection = query.getCollection()
  return collection
    .find()
    .then((entities: any[]) => _.compact(_.map(entities, (entity) => _.get(entity, KEY_PATH))))
    .catch(() => [])
    .finally(() => collection.dispose())
}

/**
 * Eligible keys minus the ones already created. Compared case-insensitively to
 * match the per-firm uniqueness constraint on `lower(key)`.
 */
export const getAvailableReferenceListKeys = (
  schemas: any[],
  existingKeys: string[]
): IReferenceListKeyOption[] => {
  const taken = new Set(_.map(existingKeys, (key) => _.toLower(key)))
  return _.reject(collectReferenceListKeys(schemas), (option) => taken.has(_.toLower(option.value)))
}

/** Maps grouped-query buckets to distinct {value,label} options. */
export const mapDistinctBuckets = (buckets: any[]): IReferenceListOption[] =>
  _.compact(
    _.map(buckets, (bucket) => {
      const value = bucket?.data
      return _.isString(value) && value ? { label: value, value } : null
    })
  )

/**
 * Distinct values already stored on a field (grouped query on the bound path), so it
 * self-suggests from prior usage; scoped to `firmId`, [] when no value path.
 * Array-of-primitive fields key under the bare dotted path — no `[*]` suffix.
 */
export const fetchDistinctFieldValues = (
  apis: any,
  entityType: string,
  valuePath: string[],
  size?: number,
  firmId?: string,
): Promise<IReferenceListOption[]> => {
  const query = new Query(apis)
    .setEntityType(entityType)
    .setGroups([{ path: valuePath.join('.'), type: 'field' }])
    .setShouldIncludeLeafEntities(false)
    .setSize(size ?? DEFAULT_DISTINCT_VALUES_SIZE)
    .setDebug({ context: 'optionSource--distinctValues' })
  if (firmId) {
    query.setFirm(firmId)
  }
  const collection = query.getCollection()
  return collection
    .find()
    .then((buckets: any[]) => mapDistinctBuckets(buckets))
    // Suggestions are advisory (unlike an authoritative reference list), so a load failure
    // degrades to "no suggestions" rather than surfacing an error.
    .catch(() => [])
    .finally(() => collection.dispose())
}

/**
 * Normalizes a creatable multi-select's values via a `transform` formula (e.g.
 * 'UPPER(TO_IDENTIFIER(arg0))') — deduped, empties dropped. `values` is the field's value array;
 * callers only wire this on multi-value tag fields.
 */
export const applyTagTransform = (transform: string | undefined, values: string[]): string[] => {
  if (!transform) {
    return values
  }
  const next = _.uniq(_.map(values, (value) => (_.isString(value) ? evaluateFormula(transform, value) ?? value : value)))
  return _.reject(next, (value) => value === '')
}
